import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import Brand from '../components/Brand';
import { useAdminStore } from '../store/adminStore';
import { LayoutDashboard, Users, ShoppingBag, FileText, Brain, LogOut, ExternalLink, Menu, X } from 'lucide-react';

const NAV = [
  { to: '/admin/dashboard',   icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/admin/pages',       icon: FileText,        label: 'Pages' },
  { to: '/admin/ee-training', icon: Brain,           label: 'EE Training' },
  { to: '/admin/users',       icon: Users,           label: 'Users' },
  { to: '/admin/purchases',   icon: ShoppingBag,     label: 'Purchases' },
];

const linkCls = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
    isActive
      ? 'bg-gold/10 text-gold border border-gold/30'
      : 'text-ee-text2 hover:text-white hover:bg-ee-card2'
  }`;

function NavList({ admin, onNavigate, onSignOut }) {
  return (
    <>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {NAV.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink key={item.to} to={item.to} onClick={onNavigate} className={linkCls}>
              <Icon size={16} /> {item.label}
            </NavLink>
          );
        })}
        <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-ee-muted hover:text-gold">
          <ExternalLink size={14} /> View site
        </a>
      </nav>
      <div className="p-3 border-t border-ee-border">
        <div className="px-3 py-2 mb-2">
          <div className="text-xs text-ee-muted">Signed in as</div>
          <div className="text-sm text-white truncate">{admin?.name || admin?.email}</div>
          <div className="text-[10px] uppercase tracking-wider text-gold/80 mt-1">{admin?.role || 'author'}</div>
        </div>
        <button onClick={onSignOut} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-red-400 hover:bg-red-400/10">
          <LogOut size={14} /> Sign Out
        </button>
      </div>
    </>
  );
}

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminStore();
  const nav = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll while drawer is open
  useEffect(() => {
    if (drawerOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [drawerOpen]);

  // Close drawer on Escape
  useEffect(() => {
    if (!drawerOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDrawerOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const handleSignOut = () => { logout(); nav('/admin/login'); };

  return (
    <div className="min-h-screen bg-ee-bg text-white flex overflow-x-hidden">
      {/* Desktop sidebar */}
      <aside className="w-64 hidden lg:flex flex-col bg-ee-bg2 border-r border-ee-border min-h-screen sticky top-0 flex-shrink-0">
        <div className="p-4 border-b border-ee-border">
          <Brand size="sm" subtitle="The EE Control Center" />
        </div>
        <NavList admin={admin} onSignOut={handleSignOut} />
      </aside>

      {/* Main column */}
      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden sticky top-0 z-30 bg-ee-bg/95 backdrop-blur border-b border-ee-border flex items-center gap-3 p-3">
          <button
            data-testid="admin-menu-toggle"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            className="p-2 -ml-1 rounded-md text-ee-text2 hover:text-gold hover:bg-ee-card2 transition-colors"
          >
            <Menu size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <Brand size="sm" subtitle="Control Center" />
          </div>
        </div>
        <div className="flex-1 min-w-0">
          {children}
        </div>
      </main>

      {/* Mobile slide-in drawer */}
      {drawerOpen && (
        <>
          {/* Backdrop */}
          <button
            aria-label="Close menu"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden fixed inset-0 z-40 bg-black/70 backdrop-blur-sm"
          />
          {/* Panel */}
          <aside
            role="dialog"
            aria-modal="true"
            className="lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-ee-bg2 border-r border-ee-border flex flex-col animate-slide-in"
          >
            <div className="p-4 border-b border-ee-border flex items-start justify-between gap-2">
              <Brand size="sm" subtitle="The EE Control Center" />
              <button
                onClick={() => setDrawerOpen(false)}
                aria-label="Close menu"
                className="p-1 -mr-1 rounded text-ee-muted hover:text-white hover:bg-ee-card2"
              >
                <X size={18} />
              </button>
            </div>
            <NavList
              admin={admin}
              onNavigate={() => setDrawerOpen(false)}
              onSignOut={() => { setDrawerOpen(false); handleSignOut(); }}
            />
          </aside>
        </>
      )}
    </div>
  );
}
